import type {
  CoachServiceAssignmentCommand,
  CoachServiceAssignmentContext,
  LegacyCoachServiceAssignmentCommand,
} from "./api";

export const COACH_SERVICE_ASSIGNMENT_RECOVERY_STORAGE_KEY =
  "billiard.ops-web.coach-service-assignment-recovery.v1";

export type CoachServiceAssignmentRecoveryState =
  | "CLAIMED"
  | "OUTCOME_UNKNOWN"
  | "RETRY_ALLOWED"
  | "LEGACY_GET_ONLY";

export interface ReplayableCoachServiceAssignmentRecovery extends CoachServiceAssignmentContext {
  command: CoachServiceAssignmentCommand;
  state: Exclude<CoachServiceAssignmentRecoveryState, "LEGACY_GET_ONLY">;
}

export interface LegacyCoachServiceAssignmentRecovery extends CoachServiceAssignmentContext {
  command: LegacyCoachServiceAssignmentCommand;
  state: "LEGACY_GET_ONLY";
}

export type CoachServiceAssignmentRecovery =
  | ReplayableCoachServiceAssignmentRecovery
  | LegacyCoachServiceAssignmentRecovery;

export type CoachServiceAssignmentRecoveryView =
  | { status: "NONE"; canRetry: false }
  | { status: "OWNED"; recovery: CoachServiceAssignmentRecovery; canRetry: boolean }
  | { status: "CONTEXT_MISMATCH" | "COMMAND_MISMATCH"; canRetry: false }
  | { status: "STORAGE_UNAVAILABLE"; canRetry: false; pendingOutcomeMayExist: boolean };

export type CoachServiceAssignmentResolution<TResult> =
  | { status: "EXECUTED"; result: TResult }
  | { status: "RETRY_ALLOWED"; recovery: CoachServiceAssignmentRecovery }
  | { status: "LEGACY_NOT_FOUND_REQUIRES_NEW_REQUEST" }
  | { status: "FAIL_CLOSED" };

let pendingRecovery: CoachServiceAssignmentRecovery | null = null;
let dispatchingRecovery: CoachServiceAssignmentRecovery | null = null;
let hydrated = false;
let storageUnavailable = false;
let pendingOutcomeMayExist = false;

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function hasExactKeys(value: Record<string, unknown>, keys: readonly string[]): boolean {
  const actual = Object.keys(value).sort();
  const expected = [...keys].sort();
  return actual.length === expected.length && actual.every((key, index) => key === expected[index]);
}

function contextIsValid(value: unknown): value is CoachServiceAssignmentContext {
  return (
    isRecord(value) &&
    hasExactKeys(value, ["tenantId", "storeId", "actorId"]) &&
    typeof value.tenantId === "string" &&
    value.tenantId.length > 0 &&
    typeof value.storeId === "string" &&
    value.storeId.length > 0 &&
    typeof value.actorId === "string" &&
    value.actorId.length > 0
  );
}

function commandIsValid(
  value: unknown,
  context: CoachServiceAssignmentContext,
): value is CoachServiceAssignmentCommand {
  if (
    !isRecord(value) ||
    !isRecord(value.context) ||
    !isRecord(value.request) ||
    !isRecord(value.expected) ||
    !hasExactKeys(value, ["context", "request", "expected"]) ||
    !hasExactKeys(value.context, ["tenantId", "storeId", "actorId"]) ||
    !hasExactKeys(value.request, [
      "sessionId",
      "coachId",
      "expectedCoachId",
      "assignmentType",
      "serviceRuleId",
      "expectedPriceFen",
      "pricingToken",
      "previewToken",
      ...(Object.hasOwn(value.request, "reason") ? ["reason"] : []),
      ...(Object.hasOwn(value.request, "customerRequestEventId") ? ["customerRequestEventId"] : []),
      "idempotencyKey",
    ]) ||
    !hasExactKeys(value.expected, [
      "coachId",
      "coachDisplayName",
      "tableNumber",
      "serviceTypeName",
      "priceFen",
    ])
  ) {
    return false;
  }
  const request = value.request;
  const expected = value.expected;
  return (
    value.context.tenantId === context.tenantId &&
    value.context.storeId === context.storeId &&
    value.context.actorId === context.actorId &&
    typeof request.sessionId === "string" &&
    request.sessionId.length > 0 &&
    request.expectedCoachId === expected.coachId &&
    ((request.assignmentType === "ROTATION" && request.coachId === null) ||
      (request.assignmentType === "REQUESTED" && request.coachId === expected.coachId)) &&
    typeof request.serviceRuleId === "string" &&
    request.serviceRuleId.length > 0 &&
    request.expectedPriceFen === expected.priceFen &&
    typeof request.pricingToken === "string" &&
    /^[a-f0-9]{64}$/.test(request.pricingToken) &&
    typeof request.previewToken === "string" &&
    /^[a-f0-9]{64}$/.test(request.previewToken) &&
    (request.reason === undefined ||
      request.reason === null ||
      (typeof request.reason === "string" &&
        request.reason === request.reason.trim() &&
        [...request.reason].length >= 2 &&
        [...request.reason].length <= 500)) &&
    (request.customerRequestEventId === undefined ||
      (typeof request.customerRequestEventId === "string" &&
        request.customerRequestEventId.length >= 8 &&
        request.customerRequestEventId.length <= 128)) &&
    typeof request.idempotencyKey === "string" &&
    request.idempotencyKey.length >= 8 &&
    request.idempotencyKey.length <= 128 &&
    typeof expected.coachId === "string" &&
    expected.coachId.length > 0 &&
    typeof expected.coachDisplayName === "string" &&
    expected.coachDisplayName.length > 0 &&
    typeof expected.tableNumber === "string" &&
    expected.tableNumber.length > 0 &&
    typeof expected.serviceTypeName === "string" &&
    expected.serviceTypeName.length > 0 &&
    typeof expected.priceFen === "number" &&
    Number.isSafeInteger(expected.priceFen) &&
    expected.priceFen >= 0
  );
}

function legacyCommandIsValid(
  value: unknown,
  context: CoachServiceAssignmentContext,
): value is LegacyCoachServiceAssignmentCommand {
  if (
    !isRecord(value) ||
    !isRecord(value.context) ||
    !isRecord(value.request) ||
    !isRecord(value.expected) ||
    !hasExactKeys(value, ["context", "request", "expected"]) ||
    !hasExactKeys(value.context, ["tenantId", "storeId", "actorId"]) ||
    !hasExactKeys(value.request, [
      "sessionId",
      "coachId",
      "expectedCoachId",
      "assignmentType",
      "serviceRuleId",
      "expectedPriceFen",
      "pricingToken",
      ...(Object.hasOwn(value.request, "reason") ? ["reason"] : []),
      ...(Object.hasOwn(value.request, "customerRequestEventId") ? ["customerRequestEventId"] : []),
      "idempotencyKey",
    ]) ||
    !hasExactKeys(value.expected, [
      "coachId",
      "coachDisplayName",
      "tableNumber",
      "serviceTypeName",
      "priceFen",
    ])
  ) {
    return false;
  }
  const request = value.request;
  const expected = value.expected;
  return (
    value.context.tenantId === context.tenantId &&
    value.context.storeId === context.storeId &&
    value.context.actorId === context.actorId &&
    typeof request.sessionId === "string" &&
    request.sessionId.length > 0 &&
    request.expectedCoachId === expected.coachId &&
    ((request.assignmentType === "ROTATION" && request.coachId === null) ||
      (request.assignmentType === "REQUESTED" && request.coachId === expected.coachId)) &&
    typeof request.serviceRuleId === "string" &&
    request.serviceRuleId.length > 0 &&
    request.expectedPriceFen === expected.priceFen &&
    typeof request.pricingToken === "string" &&
    /^[a-f0-9]{64}$/.test(request.pricingToken) &&
    (request.reason === undefined ||
      request.reason === null ||
      (typeof request.reason === "string" &&
        request.reason === request.reason.trim() &&
        [...request.reason].length >= 2 &&
        [...request.reason].length <= 500)) &&
    (request.customerRequestEventId === undefined ||
      (typeof request.customerRequestEventId === "string" &&
        request.customerRequestEventId.length >= 8 &&
        request.customerRequestEventId.length <= 128)) &&
    typeof request.idempotencyKey === "string" &&
    request.idempotencyKey.length >= 8 &&
    request.idempotencyKey.length <= 128 &&
    typeof expected.coachId === "string" &&
    expected.coachId.length > 0 &&
    typeof expected.coachDisplayName === "string" &&
    expected.coachDisplayName.length > 0 &&
    typeof expected.tableNumber === "string" &&
    expected.tableNumber.length > 0 &&
    typeof expected.serviceTypeName === "string" &&
    expected.serviceTypeName.length > 0 &&
    typeof expected.priceFen === "number" &&
    Number.isSafeInteger(expected.priceFen) &&
    expected.priceFen >= 0
  );
}

function immutableReplayableRecovery(
  context: CoachServiceAssignmentContext,
  command: CoachServiceAssignmentCommand,
  state: ReplayableCoachServiceAssignmentRecovery["state"],
): ReplayableCoachServiceAssignmentRecovery {
  const value = structuredClone({
    ...context,
    command,
    state,
  }) as ReplayableCoachServiceAssignmentRecovery;
  const freezeDeep = (item: unknown): void => {
    if (!item || typeof item !== "object" || Object.isFrozen(item)) return;
    for (const child of Object.values(item)) freezeDeep(child);
    Object.freeze(item);
  };
  freezeDeep(value);
  return value;
}

function immutableLegacyRecovery(
  context: CoachServiceAssignmentContext,
  command: LegacyCoachServiceAssignmentCommand,
): LegacyCoachServiceAssignmentRecovery {
  const value = structuredClone({
    ...context,
    command,
    state: "LEGACY_GET_ONLY",
  }) as LegacyCoachServiceAssignmentRecovery;
  const freezeDeep = (item: unknown): void => {
    if (!item || typeof item !== "object" || Object.isFrozen(item)) return;
    for (const child of Object.values(item)) freezeDeep(child);
    Object.freeze(item);
  };
  freezeDeep(value);
  return value;
}

function parseStored(value: unknown): CoachServiceAssignmentRecovery | null {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, ["version", "recovery"]) ||
    (value.version !== 1 && value.version !== 2) ||
    !isRecord(value.recovery) ||
    !hasExactKeys(value.recovery, ["tenantId", "storeId", "actorId", "command", "state"])
  ) {
    return null;
  }
  const context = {
    tenantId: value.recovery.tenantId,
    storeId: value.recovery.storeId,
    actorId: value.recovery.actorId,
  };
  if (!contextIsValid(context)) {
    return null;
  }
  const state = String(value.recovery.state);
  if (
    commandIsValid(value.recovery.command, context) &&
    ["CLAIMED", "OUTCOME_UNKNOWN", "RETRY_ALLOWED"].includes(state)
  ) {
    return immutableReplayableRecovery(
      context,
      value.recovery.command,
      state as ReplayableCoachServiceAssignmentRecovery["state"],
    );
  }
  if (
    value.version === 1 &&
    legacyCommandIsValid(value.recovery.command, context) &&
    ["CLAIMED", "OUTCOME_UNKNOWN", "RETRY_ALLOWED"].includes(state)
  ) {
    return immutableLegacyRecovery(context, value.recovery.command);
  }
  return null;
}

function storageAccess(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return typeof sessionStorage === "undefined" ? null : sessionStorage;
  } catch {
    return null;
  }
}

function recoveryEquals(
  left: CoachServiceAssignmentRecovery,
  right: CoachServiceAssignmentRecovery,
): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

function readStored(storage: Storage): CoachServiceAssignmentRecovery | null {
  const encoded = storage.getItem(COACH_SERVICE_ASSIGNMENT_RECOVERY_STORAGE_KEY);
  return encoded === null ? null : parseStored(JSON.parse(encoded) as unknown);
}

function saveStored(recovery: CoachServiceAssignmentRecovery): boolean {
  if (typeof window === "undefined") return true;
  const storage = storageAccess();
  if (!storage) return false;
  try {
    storage.setItem(
      COACH_SERVICE_ASSIGNMENT_RECOVERY_STORAGE_KEY,
      JSON.stringify({ version: 2, recovery }),
    );
    const saved = readStored(storage);
    return saved !== null && recoveryEquals(saved, recovery);
  } catch {
    return false;
  }
}

function hydrate(): void {
  if (hydrated) return;
  hydrated = true;
  if (typeof window === "undefined") return;
  const storage = storageAccess();
  if (!storage) {
    storageUnavailable = true;
    return;
  }
  try {
    const encoded = storage.getItem(COACH_SERVICE_ASSIGNMENT_RECOVERY_STORAGE_KEY);
    if (encoded === null) return;
    pendingOutcomeMayExist = true;
    const stored = parseStored(JSON.parse(encoded) as unknown);
    if (!stored) {
      storageUnavailable = true;
      return;
    }
    pendingRecovery =
      stored.state === "CLAIMED"
        ? immutableReplayableRecovery(stored, stored.command, "OUTCOME_UNKNOWN")
        : stored;
    if (stored.state === "CLAIMED" && !saveStored(pendingRecovery)) storageUnavailable = true;
  } catch {
    storageUnavailable = true;
  }
}

function contextMatches(
  recovery: CoachServiceAssignmentRecovery,
  context: CoachServiceAssignmentContext,
): boolean {
  return (
    recovery.tenantId === context.tenantId &&
    recovery.storeId === context.storeId &&
    recovery.actorId === context.actorId
  );
}

function unavailableView(): CoachServiceAssignmentRecoveryView {
  return { status: "STORAGE_UNAVAILABLE", canRetry: false, pendingOutcomeMayExist };
}

export function coachServiceAssignmentContext(operator: {
  tenantId: string;
  storeId: string;
  id: string;
}): CoachServiceAssignmentContext {
  return { tenantId: operator.tenantId, storeId: operator.storeId, actorId: operator.id };
}

export function readCoachServiceAssignmentRecovery(
  context: CoachServiceAssignmentContext,
): CoachServiceAssignmentRecoveryView {
  hydrate();
  if (storageUnavailable || !contextIsValid(context)) return unavailableView();
  if (!pendingRecovery) return { status: "NONE", canRetry: false };
  if (!contextMatches(pendingRecovery, context)) {
    return { status: "CONTEXT_MISMATCH", canRetry: false };
  }
  return {
    status: "OWNED",
    recovery: pendingRecovery,
    canRetry: pendingRecovery.state === "RETRY_ALLOWED",
  };
}

export function claimCoachServiceAssignmentRecovery(
  context: CoachServiceAssignmentContext,
  command: CoachServiceAssignmentCommand,
): CoachServiceAssignmentRecoveryView {
  const current = readCoachServiceAssignmentRecovery(context);
  if (current.status === "OWNED") {
    return JSON.stringify(current.recovery.command) === JSON.stringify(command)
      ? current
      : { status: "COMMAND_MISMATCH", canRetry: false };
  }
  if (current.status !== "NONE") return current;
  if (!commandIsValid(command, context)) {
    storageUnavailable = true;
    return unavailableView();
  }
  const recovery = immutableReplayableRecovery(context, command, "CLAIMED");
  if (!saveStored(recovery)) {
    storageUnavailable = true;
    return unavailableView();
  }
  pendingRecovery = recovery;
  pendingOutcomeMayExist = true;
  return { status: "OWNED", recovery, canRetry: false };
}

function verify(
  recovery: CoachServiceAssignmentRecovery,
  context: CoachServiceAssignmentContext,
): boolean {
  hydrate();
  if (storageUnavailable || pendingRecovery !== recovery || !contextMatches(recovery, context)) {
    return false;
  }
  if (typeof window === "undefined") return true;
  const storage = storageAccess();
  if (!storage) return false;
  try {
    const stored = readStored(storage);
    if (!stored || !recoveryEquals(stored, recovery)) {
      storageUnavailable = true;
      return false;
    }
    return true;
  } catch {
    storageUnavailable = true;
    return false;
  }
}

export function beginCoachServiceAssignmentDispatch(
  recovery: CoachServiceAssignmentRecovery,
  context: CoachServiceAssignmentContext,
): boolean {
  if (
    dispatchingRecovery ||
    recovery.state === "OUTCOME_UNKNOWN" ||
    recovery.state === "LEGACY_GET_ONLY" ||
    !verify(recovery, context)
  ) {
    return false;
  }
  dispatchingRecovery = recovery;
  return true;
}

export function finishCoachServiceAssignmentDispatch(
  recovery: CoachServiceAssignmentRecovery,
  context: CoachServiceAssignmentContext,
): boolean {
  if (dispatchingRecovery !== recovery || !contextMatches(recovery, context)) return false;
  dispatchingRecovery = null;
  return true;
}

export function markCoachServiceAssignmentOutcomeUnknown(
  recovery: CoachServiceAssignmentRecovery,
  context: CoachServiceAssignmentContext,
): boolean {
  if (recovery.state === "LEGACY_GET_ONLY" || !verify(recovery, context)) return false;
  const unknown = immutableReplayableRecovery(context, recovery.command, "OUTCOME_UNKNOWN");
  if (!saveStored(unknown)) {
    storageUnavailable = true;
    return false;
  }
  pendingRecovery = unknown;
  pendingOutcomeMayExist = true;
  return true;
}

export function clearCoachServiceAssignmentRecovery(
  recovery: CoachServiceAssignmentRecovery,
  context: CoachServiceAssignmentContext,
): boolean {
  if (!verify(recovery, context)) return false;
  if (typeof window !== "undefined") {
    const storage = storageAccess();
    if (!storage) return false;
    try {
      storage.removeItem(COACH_SERVICE_ASSIGNMENT_RECOVERY_STORAGE_KEY);
      if (storage.getItem(COACH_SERVICE_ASSIGNMENT_RECOVERY_STORAGE_KEY) !== null) return false;
    } catch {
      storageUnavailable = true;
      return false;
    }
  }
  if (dispatchingRecovery === recovery) dispatchingRecovery = null;
  pendingRecovery = null;
  pendingOutcomeMayExist = false;
  return true;
}

export function reconcileCoachServiceAssignmentAuthority<TResult>(
  recovery: CoachServiceAssignmentRecovery,
  context: CoachServiceAssignmentContext,
  result: TResult | null,
): CoachServiceAssignmentResolution<TResult> {
  if (recovery.state === "LEGACY_GET_ONLY") {
    if (!verify(recovery, context) || !clearCoachServiceAssignmentRecovery(recovery, context)) {
      return { status: "FAIL_CLOSED" };
    }
    return result === null
      ? { status: "LEGACY_NOT_FOUND_REQUIRES_NEW_REQUEST" }
      : { status: "EXECUTED", result };
  }
  if (recovery.state !== "OUTCOME_UNKNOWN" || !verify(recovery, context)) {
    return { status: "FAIL_CLOSED" };
  }
  if (result !== null) {
    return clearCoachServiceAssignmentRecovery(recovery, context)
      ? { status: "EXECUTED", result }
      : { status: "FAIL_CLOSED" };
  }
  const retryable = immutableReplayableRecovery(context, recovery.command, "RETRY_ALLOWED");
  if (!saveStored(retryable)) {
    storageUnavailable = true;
    return { status: "FAIL_CLOSED" };
  }
  pendingRecovery = retryable;
  return { status: "RETRY_ALLOWED", recovery: retryable };
}

export function resetCoachServiceAssignmentRecoveryForTests(): void {
  pendingRecovery = null;
  dispatchingRecovery = null;
  hydrated = false;
  storageUnavailable = false;
  pendingOutcomeMayExist = false;
  try {
    storageAccess()?.removeItem(COACH_SERVICE_ASSIGNMENT_RECOVERY_STORAGE_KEY);
  } catch {
    // The next test starts from a fresh fail-closed state.
  }
}

export function resetCoachServiceAssignmentRecoveryMemoryForTests(): void {
  pendingRecovery = null;
  dispatchingRecovery = null;
  hydrated = false;
  storageUnavailable = false;
  pendingOutcomeMayExist = false;
}
